import { useState } from 'react';
import DocsPromptBlock from './DocsPromptBlock';

export default function DocsFeatureGuide({ guide, interstitial = null, contentBeforeUsage = null }) {
  const [surface, setSurface] = useState('client');
  const steps = guide.steps || [
    { number: '1', title: 'Set it up', body: guide.setup, href: '#set-it-up', cta: 'See what you need' },
    { number: '2', title: 'Use it from an AI client', body: 'Prompt Pabs from the connected AI client where the team is already working.', href: '#use-it-with-your-team', cta: 'Copy the prompt' },
    { number: '3', title: 'Check the result', body: guide.result, href: '#check-the-result', cta: 'See what to verify' },
  ];

  return (
    <div className="mb-10">
      <div className={`${interstitial ? 'mb-0' : 'mb-8'} grid grid-cols-1 gap-3 sm:grid-cols-3`}>
        {steps.map(({ number, title, body, href, cta }) => (
          <div key={number} className="flex flex-col rounded-xl border border-gray-200 p-4 dark:border-brand-50/10 dark:bg-brand-800/20">
            <span className="font-mono text-[11px] text-gray-400 dark:text-brand-600">0{number}</span>
            <h2 className="mt-3 text-[14px] font-semibold text-gray-900 dark:text-brand-100">{title}</h2>
            <p className="mt-2 text-[13px] leading-relaxed text-gray-500 dark:text-brand-500">{body}</p>
            {href && cta && (
              <a
                href={href}
                className="mt-auto pt-4 text-[12px] font-semibold text-gray-700 underline decoration-gray-300 underline-offset-4 transition-colors hover:text-gray-950 hover:decoration-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 dark:text-brand-300 dark:decoration-brand-700 dark:hover:text-brand-50 dark:hover:decoration-brand-400"
              >
                {cta}
              </a>
            )}
          </div>
        ))}
      </div>

      {interstitial ? (
        <div className="mb-8">{interstitial}</div>
      ) : (
        <section className="mb-8" aria-labelledby="set-it-up">
          <h2 id="set-it-up" className="mb-4 mt-10 scroll-mt-20 text-xl font-semibold leading-snug tracking-tight text-gray-900 dark:text-brand-50">
            Set it up first
          </h2>
          <p className="mb-4 text-[15px] leading-relaxed text-gray-600 dark:text-brand-400">{guide.setup}</p>
        </section>
      )}

      {contentBeforeUsage && <div className="mb-8">{contentBeforeUsage}</div>}

      <h2 id="use-it-with-your-team" className="mb-4 mt-10 scroll-mt-20 text-xl font-semibold leading-snug tracking-tight text-gray-900 dark:text-brand-50">
        Use it with your team
      </h2>
      <div className="mb-4 inline-flex rounded-lg border border-gray-200 p-1 dark:border-brand-50/10">
        {[
          ['client', guide.clientLabel || 'From an AI client'],
          ['app', guide.appLabel || 'In the Pabs interface'],
        ].map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => setSurface(value)}
            className={`rounded-md px-3 py-1.5 text-[12px] font-semibold transition-colors ${
              surface === value
                ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                : 'text-gray-500 hover:text-gray-900 dark:text-brand-500 dark:hover:text-brand-100'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {surface === 'app' ? (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-5 dark:border-brand-50/10 dark:bg-brand-800/30">
          <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-brand-600">{guide.appEyebrow || 'In the Pabs interface'}</p>
          <p className="m-0 text-[14px] leading-relaxed text-gray-600 dark:text-brand-400">{guide.app}</p>
        </div>
      ) : (
        <DocsPromptBlock surfaceOnly="client" forcePrompt>{guide.prompt}</DocsPromptBlock>
      )}

      <section className="mt-8 border-t border-gray-200 pt-8 dark:border-brand-50/10" aria-labelledby="check-the-result">
        <h2 id="check-the-result" className="m-0 text-xl font-semibold leading-snug tracking-tight text-gray-900 dark:text-brand-50">
          Check the result
        </h2>
        <p className="mb-0 mt-3 text-[15px] leading-relaxed text-gray-600 dark:text-brand-400">{guide.result}</p>
      </section>
    </div>
  );
}
